import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, {
  Mapped,
  Optional,
  Silent,
  Union,
} from "@/__main__/data-model.mjs";
import {
  BodyPartBE,
  ExitReason,
  FactionBE,
  GameMode,
  ItemType,
} from "@/game-eft/constants/constants.mjs";
import {
  DataBricksPath,
  fixDataLakeResponse,
} from "@/util/fix-data-lake-response.mjs";

// Common
const faction = Union([FactionBE.BEAR, FactionBE.SCAV, FactionBE.USEC]);
const gameMode = Union([GameMode.ARENA, GameMode.DEFAULT]);
const exitReason = Union([
  ExitReason.SURVIVED,
  ExitReason.KILLED,
  ExitReason.LEFT,
  ExitReason.MISSING_IN_ACTION,
  ExitReason.RUNNER,
]);
const bodyPart = Union([
  BodyPartBE.BODY,
  BodyPartBE.CHEST,
  BodyPartBE.HEAD,
  BodyPartBE.LEFT_ARM,
  BodyPartBE.RIGHT_ARM,
  BodyPartBE.LEFT_LEG,
  BodyPartBE.RIGHT_LEG,
  BodyPartBE.STOMACH,
]);
const itemType = Union([
  ItemType.AMMO,
  ItemType.AMMO_BOX,
  ItemType.ARMORED_EQUIPMENT,
  ItemType.ARM_BAND,
  ItemType.ASSAULT_CARBINE,
  ItemType.ASSAULT_RIFLE,
  ItemType.BACKPACK,
  ItemType.BARTER,
  ItemType.DRINK,
  ItemType.DRUGS,
  ItemType.FOOD,
  ItemType.GRENADE_LAUNCHER,
  ItemType.HEADPHONES,
  ItemType.HIDEOUT_CONTAINER,
  ItemType.INFO,
  ItemType.ITEM,
  ItemType.KEY,
  ItemType.KNIFE,
  ItemType.MACHINE_GUN,
  ItemType.MAP,
  ItemType.MARKSMAN_RIFLE,
  ItemType.MEDICAL,
  ItemType.MEDS,
  ItemType.MED_KIT,
  ItemType.MOD,
  ItemType.MONEY,
  ItemType.PISTOL,
  ItemType.POCKETS,
  ItemType.REOVLER,
  ItemType.SEARCHABLE_ITEM,
  ItemType.SHOTGUN,
  ItemType.SIMPLE_CONTAINER,
  ItemType.SMG,
  ItemType.SNIPER_RIFLE,
  ItemType.SPECIAL,
  ItemType.STIMULATOR,
  ItemType.THROWABLE_WEAPON,
  ItemType.VEST,
  ItemType.WEAPON,
]);

/**
 * All of the model definitions, validators, transformers and types declared here are specifically from BE
 * Do not add anything here from RE, that should go into the appropriate file within models
 */
const playerFactionStat = {
  damage: Number,
  experience: Number,
  faction,
  level: Number,
  loot: Number,
  matches: Number,
  kills: Number,
  updatedAt: Date,
} satisfies ModelDefinition;

const profile = {
  accountId: String,
  id: String,
  isBot: Boolean,
  updatedAt: Date,
  username: String,
} satisfies ModelDefinition;

const playerMatch = {
  gameId: String,
  startedAt: String,
  faction,
} satisfies ModelDefinition;

const playerMatchFull = {
  killsBody: Number,
  season: {
    id: String,
  },
  shotsLanded: Number,
  totalShots: Number,
  killsBodyHist: Number,
  killsHeadHist: Number,
  hitsBodyHist: Number,
  hitsRightLegHist: Number,
  duration: Number,
  hitsLeftLeg: Number,
  hitsRightArm: Number,
  exitReason: Optional(exitReason),
  faction,
  gameId: String,
  hitsBody: Number,
  hitsLeftArm: Number,
  hitsLeftArmHist: Number,
  hitsRightLeg: Number,
  damageBodyHist: Number,
  hitsChest: Number,
  hitsStomachHist: Number,
  hitsHead: Number,
  extractionPoint: Silent(String),
  gameMap: {
    id: String,
    key: String,
    name: String,
  },
  groupId: Silent(String),
  hitsHeadHist: Number,
  weaponKills: [
    {
      killed: {
        isBot: Boolean,
        accountId: String,
      },
      killer: {
        isBot: Boolean,
        accountId: String,
      },
      weapon: {
        id: String,
        longName: String,
        shortName: String,
        uuid: String,
      },
    },
  ],
  hitsRightArmHist: Number,
  killsHead: Number,
  damageBody: Number,
  hitsChestHist: Number,
  damageHead: Number,
  player: {
    accountId: String,
    isBot: Boolean,
    username: String,
  },
  weaponHits: [
    {
      bodyPart,
      damage: Number,
      gameMap: {
        id: String,
        key: String,
        name: String,
      },
      hits: Number,
      id: String,
      kills: Number,
      weapon: {
        id: String,
        longName: String,
        shortName: String,
        uuid: String,
      },
    },
  ],
  damageHeadHist: Number,
  hitsStomach: Number,
  hitsLeftLegHist: Number,
} satisfies ModelDefinition;

export const matchModel = {
  gameId: String,
  gameMap: {
    id: String,
    key: String,
    name: String,
  },
  gameMode,
  playerMatches: [playerMatchFull],
  seasonId: String,
  startedAt: String,
  updatedAt: String,
} satisfies ModelDefinition;

const playerSeasonStat = {
  accountId: String,
  characterId: String,
  damage: Number,
  deaths: Number,
  experience: Number,
  faction,
  gameMode,
  headshots: Number,
  id: String, // Important for updating the player stats for end of game event via live game
  kills: Number,
  level: Number,
  loot: Number,
  matches: Number,
  player: {
    accountId: String,
    id: String,
    isBot: Boolean,
    username: String,
  },
  season: {
    id: String,
  },
  updatedAt: String,
  username: String,
} satisfies ModelDefinition;

const createPlayerSeasonStatInput = {
  accountId: String,
  characterId: String,
  createdAt: String,
  damage: Number,
  deaths: Number,
  experience: Number,
  faction,
  gameMode,
  headshots: Number,
  kills: Number,
  level: Number,
  loot: Number,
  matches: Number,
  username: String,
} satisfies ModelDefinition;

const updatePlayerSeasonStatInput = {
  accountId: String,
  damage: Number,
  deaths: Number,
  experience: Number,
  faction,
  gameMode,
  headshots: Number,
  kills: Number,
  level: Number,
  loot: Number,
  matches: Number,
  username: String,
} satisfies ModelDefinition;

export const createMatchInput = {
  characterId: String,
  exitReason,
  extractionPoint: String,
  faction,
  gameMode,
  gameStartedAt: String,
  kills: [
    {
      killedCharacterId: String,
      killerCharacterId: String,
      weaponId: String,
    },
  ],
  map: String,
  matchId: String,
  matchPlayers: [
    {
      accountId: String,
      afflictions: Number,
      characterId: String,
      createdAt: String,
      damageTaken: Number,
      experience: Number,
      faction,
      groupId: String,
      level: Number,
      shotsLanded: Number,
      totalShots: Number,
      username: String,
    },
  ],
  playerMatchStats: [
    {
      characterId: String,
      duration: Number,
      playerMatchWeaponStats: [
        {
          bodyParts: [
            {
              bodyPart,
              damage: Number,
              hits: Number,
              kills: Number,
            },
          ],
          name: String,
          shortName: String,
          type: itemType,
          weaponId: String,
        },
      ],
    },
  ],
} satisfies ModelDefinition;

export const playerWeaponStat = {
  afflictions: Number,
  count: Number,
  damageDealt: Number,
  damageTaken: Number,
  duration: Number,
  gameMode: Optional(gameMode),
  hitsBody: Number,
  hitsChest: Number,
  hitsHead: Number,
  hitsLeftArm: Number,
  hitsLeftLeg: Number,
  hitsRightArm: Number,
  hitsRightLeg: Number,
  hitsStomach: Number,
  kills: Number,
  shotsLanded: Number,
  survived: Number,
  totalShots: Number,
  weapon: {
    id: String,
    longName: String,
    shortName: String,
    uuid: String,
  },
} satisfies ModelDefinition;

const playerMapStat = {
  count: Number,
  gameMap: {
    id: String,
    key: String,
    name: String,
  },
  shotsLanded: Number,
  survived: Number,
  totalShots: Number,
  duration: Number,
  kills: Number,
} satisfies ModelDefinition;

const season = {
  endAt: Optional(String),
  id: String,
  startAt: String,
} satisfies ModelDefinition;

const item = {
  id: String,
  longName: String,
  shortName: String,
  type: itemType,
  uuid: String,
} satisfies ModelDefinition;

// Model definitions into model validators
// Want to create transformations?
// Create your model transformations after the model validator declarations
export const ProfileValidate = createModel({
  data: {
    player: profile,
  },
}).transform(profile, (response) => response.data.player);
export const MatchlistValidate = createModel({
  data: {
    player: {
      playerMatches: [playerMatch],
    },
  },
}).transform([playerMatch], (response) =>
  response.data.player.playerMatches.sort(
    (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
  ),
);
export const MatchValidate = createModel({
  data: { match: matchModel },
}).transform(matchModel, (response) => response.data.match);
export const PlayerSeasonStatValidate = createModel({
  data: { playerSeasonStat: Optional(playerSeasonStat) },
}).transform(playerSeasonStat, (response) => response.data.playerSeasonStat);
export const PlayerWeaponStatsValidate = createModel({
  data: { playerWeaponStats: [playerWeaponStat] },
}).transform([playerWeaponStat], (response) => response.data.playerWeaponStats);
export const PlayerFactionStatsValidate = createModel({
  data: {
    player: {
      seasonStats: [playerFactionStat],
    },
  },
}).transform(
  [playerFactionStat],
  (response) => response.data.player.seasonStats,
);
export const PlayerMapStatsValidate = createModel({
  data: {
    playerGameMapStats: [playerMapStat],
  },
}).transform([playerMapStat], (response) => response.data.playerGameMapStats);
export const CreatePlayerSeasonStatInputValidate = createModel(
  createPlayerSeasonStatInput,
);
export const UpdatePlayerSeasonStatInputValidate = createModel(
  updatePlayerSeasonStatInput,
);
export const CreateMatchInputValidate = createModel(createMatchInput);
export const SeasonsValidate = createModel({
  data: { seasons: [season] },
}).transform([season], (response) => {
  const data = response.data.seasons;
  data.sort(
    (a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime(),
  );
  return data;
});
export const ItemsValidate = createModel({
  data: {
    items: [item],
  },
}).transform(
  Mapped({
    key: String,
    value: item,
  }),
  (response) =>
    response.data.items.reduce((acc, cur) => {
      if (acc[cur.id]) return acc;
      acc[cur.id] = cur;
      return acc;
    }, Object.create(null)),
);

// Datalake payload orders
const LeaderboardsWeaponPayloadOrder = [
  "season_id",
  "weapon_id",
  "game_mode",
  "faction",
  "rank",
  "num_raids",
  "avg_kills_per_raid",
  "avg_accuracy",
  "avg_headshot_accuracy",
  "avg_damage_dealt_per_raid",
  "avg_duration_per_raid",
  "avg_afflictions_per_raid",
  "filters",
] as const;
const LeaderboardsMapPayloadOrder = [
  "season_id",
  "game_mode",
  "faction",
  "game_map_name",
  "num_raids",
  "survival_rate",
  "sum_duration",
  "avg_duration_per_raid",
  "num_kills",
  "avg_kills_per_raid",
  "sum_loot",
  "avg_loot_per_raid",
  "filters",
] as const;
const LeaderboardsPlayerPayloadOrder = [
  "season_id",
  "game_mode",
  "faction",
  "account_id",
  "ranking_metric",
  "rank",
  "page",
  "num_raids",
  "survival_rate",
  "num_kills",
  "avg_kills_per_raid",
  "sum_loot",
  "avg_loot_per_raid",
  "avg_damage_per_raid",
  "level",
  "filters",
] as const;
const leaderboardsWeapon = Mapped({
  key: Union(LeaderboardsWeaponPayloadOrder),
  value: String,
});
const leaderboardsMap = Mapped({
  key: Union(LeaderboardsMapPayloadOrder),
  value: String,
});
const leaderboardsPlayer = Mapped({
  key: Union(LeaderboardsPlayerPayloadOrder),
  value: String,
});
// Datalake transformers
export const LeaderboardsWeaponsValidate = createModel(
  DataBricksPath,
).transform([leaderboardsWeapon], (response) =>
  fixDataLakeResponse<{
    [K in (typeof LeaderboardsWeaponPayloadOrder)[number]]: number;
  }>(response, LeaderboardsWeaponPayloadOrder),
);
export const LeaderboardsMapsValidate = createModel(DataBricksPath).transform(
  [leaderboardsMap],
  (response) =>
    fixDataLakeResponse<{
      [K in (typeof LeaderboardsMapPayloadOrder)[number]]: number;
    }>(response, LeaderboardsMapPayloadOrder),
);
export const LeaderboardsPlayersValidate = createModel(
  DataBricksPath,
).transform([leaderboardsPlayer], (response) =>
  fixDataLakeResponse<{
    [K in (typeof LeaderboardsPlayerPayloadOrder)[number]]: number;
  }>(response, LeaderboardsPlayerPayloadOrder),
);

// Model validators into TypeScript types
export type Profile = FromModel<typeof ProfileValidate>;
export type Matchlist = FromModel<typeof MatchlistValidate>;
export type Match = FromModel<typeof MatchValidate>;
export type PlayerMatch = FromModel<typeof playerMatchFull>;
export type PlayerSeasonStat = FromModel<typeof PlayerSeasonStatValidate>;
export type PlayerWeaponStat = FromModel<typeof playerWeaponStat>;
export type PlayerWeaponStats = FromModel<typeof PlayerWeaponStatsValidate>;
export type PlayerFactionStat = FromModel<typeof playerFactionStat>;
export type PlayerFactionStats = FromModel<typeof PlayerFactionStatsValidate>;
export type PlayerMapStat = FromModel<typeof playerMapStat>;
export type PlayerMapStats = FromModel<typeof PlayerMapStatsValidate>;
export type CreatePlayerSeasonStatInput = FromModel<
  typeof CreatePlayerSeasonStatInputValidate
>;
export type UpdatePlayerSeasonStatInput = FromModel<
  typeof UpdatePlayerSeasonStatInputValidate
>;
export type CreateMatchInput = FromModel<typeof CreateMatchInputValidate>;
export type Seasons = FromModel<typeof SeasonsValidate>;
export type LeaderboardsWeapon = FromModel<typeof leaderboardsWeapon>;
export type LeaderboardsMap = FromModel<typeof leaderboardsMap>;
export type LeaderboardsPlayer = FromModel<typeof leaderboardsPlayer>;
export type Items = FromModel<typeof ItemsValidate>;
